/**
 * CreditConsumedToast - Brief auto-dismissing notification showing credits consumed after analysis.
 */

import { useEffect, useState } from 'react';
import { Coins, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CreditConsumedToastProps {
  creditsUsed: number;
  newBalance: number;
  /** Auto-dismiss duration in ms (default 6000) */
  duration?: number;
  onDismiss: () => void;
}

export function CreditConsumedToast({ creditsUsed, newBalance, duration = 6000, onDismiss }: CreditConsumedToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Animate in on mount
  useEffect(() => {
    const timer = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(timer);
  }, []);

  // Auto-dismiss
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(onDismiss, 300); // Wait for exit animation
    }, duration);
    return () => clearTimeout(timer);
  }, [duration, onDismiss]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(onDismiss, 300);
  };

  return (
    <div
      className={cn(
        'fixed bottom-6 right-6 z-[100] flex items-center gap-3 px-4 py-3 rounded-xl',
        'bg-surface/95 border border-border-subtle shadow-2xl backdrop-blur-sm',
        'transition-all duration-300 ease-out',
        isVisible && !isExiting
          ? 'translate-y-0 opacity-100'
          : 'translate-y-4 opacity-0'
      )}
    >
      <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
        <Coins className="w-4 h-4 text-primary" />
      </div>
      <div className="text-sm">
        <span className="text-text-primary">Analysis complete</span>
        <span className="mx-1.5 text-text-muted">·</span>
        <span className="text-primary font-medium">{creditsUsed} credits used</span>
        <span className="mx-1.5 text-text-muted">·</span>
        <span className="text-text-muted">Balance: {newBalance}</span>
      </div>
      <button
        onClick={handleDismiss}
        className="ml-1 p-1 text-text-muted hover:text-text-secondary transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
